import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  Chip,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  LinearProgress,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  Sync,
  AccountBalance,
  Warning,
  CheckCircle,
  CompareArrows,
  CloudUpload,
  History,
  AutorenewTwoTone
} from '@mui/icons-material';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { BankingService } from '../../services/integrations/BankingService';
import { ReconciliationService } from '../../services/ReconciliationService';
import { TransactionMatcher } from '../TransactionMatcher';
import { AuditTrailViewer } from '../AuditTrailViewer';
import { Transaction, ReconciliationTransaction } from '../../types/accounting';
import { BankConnection, BankTransaction } from '../../types/banking';

// Define BookTransaction type
interface BookTransaction {
  id: string;
  date: Date;
  amount: number;
  description: string;
  type: 'DEBIT' | 'CREDIT';
  category: string;
  reference: string;
  status: 'POSTED' | 'PENDING';
}

// Define a single source of truth for status values
type ReconciliationStatus = 'in_progress' | 'completed' | 'needs_review';

// Service types (matching the API response)
interface ServiceMatch {
  bankTransactionId: string;
  bookTransactionId: string;
  confidence: number;
  verified: boolean;
}

interface ServiceException {
  type: 'unmatched_bank' | 'unmatched_book' | 'amount_mismatch';
  transactionId: string;
  details: string;
}

// Component types (for UI display)
interface ReconciliationMatch {
  bankTransaction: BankTransaction;
  bookTransaction: Transaction;
  confidence: number;
  verified: boolean;
}

interface ReconciliationException {
  type: 'unmatched_bank' | 'unmatched_book' | 'amount_mismatch';
  transaction: BankTransaction | Transaction;
  details: string;
}

interface ReconciliationSessionResponse {
  id: string;
  bankBalance: number;
  bookBalance: number;
  difference: number;
  status: ReconciliationStatus;
  matches: ServiceMatch[];
  exceptions: ServiceException[];
}

interface ReconciliationSession {
  id: string;
  startDate: Date;
  endDate: Date;
  bankBalance: number;
  bookBalance: number;
  difference: number;
  status: ReconciliationStatus;
  matches: ReconciliationMatch[];
  exceptions: ReconciliationException[];
}

export interface ReconciliationDashboardProps {
  fundId: string;
  onTransactionClick: (transaction: ReconciliationTransaction) => void;
}

const mapToBookTransaction = (tx: Transaction): BookTransaction => {
  return {
    id: tx.id,
    date: tx.date,
    amount: tx.amount,
    description: tx.description,
    type: tx.type === 'standard' || tx.type === 'adjustment' ? 'DEBIT' : 'CREDIT',
    category: tx.category || 'uncategorized',
    reference: tx.metadata?.reference || tx.id,
    status: tx.status === 'posted' ? 'POSTED' : 'PENDING'
  };
};

export const ReconciliationDashboard: React.FC<ReconciliationDashboardProps> = ({
  fundId,
  onTransactionClick
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bankConnections, setBankConnections] = useState<BankConnection[]>([]);
  const [selectedBank, setSelectedBank] = useState<string | null>(null);
  const [session, setSession] = useState<ReconciliationSession | null>(null);
  const [bankTransactions, setBankTransactions] = useState<BankTransaction[]>([]);
  const [bookTransactions, setBookTransactions] = useState<Transaction[]>([]);
  const [matcherOpen, setMatcherOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<any>(null);
  const [auditTrailOpen, setAuditTrailOpen] = useState(false);
  const [dateRange, setDateRange] = useState<{ start: Date; end: Date }>({
    start: new Date(new Date().setDate(1)), // First day of current month
    end: new Date()
  });

  const bankingService = new BankingService();
  const reconciliationService = ReconciliationService.getInstance();

  useEffect(() => {
    loadBankConnections();
  }, [fundId]);

  const loadBankConnections = async () => {
    try {
      setLoading(true);
      const connections = await bankingService.getBankConnections(fundId);
      setBankConnections(connections);
      if (connections.length > 0) {
        setSelectedBank(connections[0].id);
      }
    } catch (err) {
      setError('Failed to load bank connections');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const startReconciliation = async () => {
    if (!selectedBank) return;
    
    try {
      setLoading(true);
      
      // Fetch transactions from both sources
      const [bankTxns, rawBookTxns] = await Promise.all([
        bankingService.getTransactions(selectedBank, dateRange),
        reconciliationService.getBookTransactions(fundId, dateRange)
      ]);

      // Type assertion for book transactions after fetch
      const bookTxns = rawBookTxns as unknown as Transaction[];

      // Convert book transactions to BookTransaction type for the service call
      const convertedBookTxns = bookTxns.map(tx => mapToBookTransaction(tx));

      // Start automated matching and get response
      const response = await reconciliationService.startSession({
        fundId,
        bankId: selectedBank,
        dateRange,
        bankTransactions: bankTxns,
        bookTransactions: convertedBookTxns
      });

      if (!isReconciliationSessionResponse(response)) {
        throw new Error('Invalid session response from service');
      }

      // Convert service matches to ReconciliationMatches using original transactions
      const matches: ReconciliationMatch[] = response.matches.map(match => ({
        bankTransaction: bankTxns.find(tx => tx.id === match.bankTransactionId)!,
        bookTransaction: bookTxns.find(tx => tx.id === match.bookTransactionId)!,
        confidence: match.confidence,
        verified: match.verified
      }));

      // Convert service exceptions to ReconciliationExceptions using original transactions
      const exceptions: ReconciliationException[] = response.exceptions.map(exception => ({
        type: exception.type,
        transaction: exception.type.includes('bank')
          ? bankTxns.find(tx => tx.id === exception.transactionId)!
          : bookTxns.find(tx => tx.id === exception.transactionId)!,
        details: exception.details
      }));

      // Create new session with converted data
      const newSession: ReconciliationSession = {
        id: response.id,
        startDate: dateRange.start,
        endDate: dateRange.end,
        bankBalance: response.bankBalance,
        bookBalance: response.bookBalance,
        difference: response.difference,
        status: response.status,
        matches,
        exceptions
      };

      setSession(newSession);
      setBankTransactions(bankTxns);
      setBookTransactions(bookTxns);

    } catch (err) {
      setError('Failed to start reconciliation');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const isReconciliationSessionResponse = (
    response: unknown
  ): response is ReconciliationSessionResponse => {
    const r = response as ReconciliationSessionResponse;
    return (
      r !== null &&
      typeof r === 'object' &&
      typeof r.id === 'string' &&
      typeof r.bankBalance === 'number' &&
      typeof r.bookBalance === 'number' &&
      typeof r.difference === 'number' &&
      typeof r.status === 'string' &&
      Array.isArray(r.matches) &&
      Array.isArray(r.exceptions) &&
      r.matches.every(m => 
        typeof m.bankTransactionId === 'string' &&
        typeof m.bookTransactionId === 'string' &&
        typeof m.confidence === 'number' &&
        typeof m.verified === 'boolean'
      ) &&
      r.exceptions.every(e => 
        typeof e.type === 'string' &&
        typeof e.transactionId === 'string' &&
        typeof e.details === 'string'
      )
    );
  };

  const handleAutoMatch = async () => {
    if (!session) return;

    try {
      setLoading(true);
      const rawResponse = await reconciliationService.autoMatch(session.id);
      
      // First convert the raw response to proper types
      const serviceResponse: ReconciliationSessionResponse = {
        id: rawResponse.id,
        bankBalance: Number(rawResponse.bankBalance),
        bookBalance: Number(rawResponse.bookBalance),
        difference: Number(rawResponse.difference),
        status: rawResponse.status as ReconciliationStatus,
        matches: Array.isArray(rawResponse.matches) 
          ? rawResponse.matches.map(m => ({
              bankTransactionId: m.bankTransactionId,
              bookTransactionId: m.bookTransactionId,
              confidence: m.confidence,
              verified: m.verified
            }))
          : [],
        exceptions: Array.isArray(rawResponse.exceptions)
          ? rawResponse.exceptions.map(e => ({
              type: e.type.toLowerCase() as ServiceException['type'],
              transactionId: e.transactionId,
              details: e.details
            }))
          : []
      };

      // Convert service matches to ReconciliationMatches
      const matches: ReconciliationMatch[] = serviceResponse.matches.map(match => ({
        bankTransaction: bankTransactions.find(tx => tx.id === match.bankTransactionId)!,
        bookTransaction: bookTransactions.find(tx => tx.id === match.bookTransactionId)!,
        confidence: match.confidence,
        verified: match.verified
      }));

      // Convert service exceptions to ReconciliationExceptions
      const exceptions: ReconciliationException[] = serviceResponse.exceptions.map(exception => ({
        type: exception.type,
        transaction: exception.type.includes('bank')
          ? bankTransactions.find(tx => tx.id === exception.transactionId)!
          : bookTransactions.find(tx => tx.id === exception.transactionId)!,
        details: exception.details
      }));

      // Create new session with converted data
      const updatedSession: ReconciliationSession = {
        id: serviceResponse.id,
        startDate: session.startDate,
        endDate: session.endDate,
        bankBalance: serviceResponse.bankBalance,
        bookBalance: serviceResponse.bookBalance,
        difference: serviceResponse.difference,
        status: serviceResponse.status,
        matches,
        exceptions
      };

      setSession(updatedSession);
    } catch (err) {
      setError('Auto-matching failed');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleManualMatch = async (bankTxn: BankTransaction, bookTxn: Transaction) => {
    if (!session) return;

    try {
      const rawResponse = await reconciliationService.manualMatch(session.id, {
        bankTransactionId: bankTxn.id,
        bookTransactionId: bookTxn.id
      });

      // Validate and type the response
      const response: ReconciliationSessionResponse = {
        id: rawResponse.id,
        bankBalance: Number(rawResponse.bankBalance),
        bookBalance: Number(rawResponse.bookBalance),
        difference: Number(rawResponse.difference),
        status: rawResponse.status as ReconciliationStatus,
        matches: Array.isArray(rawResponse.matches) ? rawResponse.matches : [],
        exceptions: Array.isArray(rawResponse.exceptions) 
          ? rawResponse.exceptions.map(e => ({
              type: e.type.toLowerCase() as ServiceException['type'],
              transactionId: e.transactionId,
              details: e.details
            }))
          : []
      };

      // Create new session with validated response data
      const updatedSession: ReconciliationSession = {
        id: response.id,
        startDate: session.startDate,
        endDate: session.endDate,
        bankBalance: response.bankBalance,
        bookBalance: response.bookBalance,
        difference: response.difference,
        status: response.status,
        matches: response.matches.map(match => ({
          bankTransaction: bankTransactions.find(tx => tx.id === match.bankTransactionId)!,
          bookTransaction: bookTransactions.find(tx => tx.id === match.bookTransactionId)!,
          confidence: match.confidence,
          verified: match.verified
        })),
        exceptions: response.exceptions.map(exception => ({
          type: exception.type,
          transaction: exception.type.includes('bank')
            ? bankTransactions.find(tx => tx.id === exception.transactionId)!
            : bookTransactions.find(tx => tx.id === exception.transactionId)!,
          details: exception.details
        }))
      };

      setSession(updatedSession);
    } catch (err) {
      setError('Manual matching failed');
      console.error(err);
    }
  };

  const handleCompleteReconciliation = async () => {
    if (!session) return;

    try {
      setLoading(true);
      await reconciliationService.completeSession(session.id);
    } catch (err) {
      setError('Failed to complete reconciliation');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const hasExceptions = session?.exceptions && session.exceptions.length > 0;

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 2 }}>
        <Grid container spacing={3}>
          {/* Header Section */}
          <Grid item xs={12}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">Bank Reconciliation</Typography>
              <Box>
                <Button
                  startIcon={<History />}
                  onClick={() => setAuditTrailOpen(true)}
                  sx={{ mr: 1 }}
                >
                  Audit Trail
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Sync />}
                  onClick={startReconciliation}
                  disabled={!selectedBank || loading}
                >
                  Start Reconciliation
                </Button>
              </Box>
            </Box>
          </Grid>

          {/* Bank Selection */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Select Bank Account</InputLabel>
              <Select
                value={selectedBank || ''}
                onChange={(e) => setSelectedBank(e.target.value)}
              >
                {bankConnections.map((conn) => (
                  <MenuItem key={conn.id} value={conn.id}>
                    {conn.provider}
                    <Chip
                      size="small"
                      label={conn.status}
                      color={conn.status === 'active' ? 'success' : 'error'}
                      sx={{ ml: 1 }}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Session Status */}
          {session && (
            <Grid item xs={12}>
              <Alert 
                severity={session.difference === 0 ? 'success' : 'warning'}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={handleAutoMatch}
                    disabled={loading}
                  >
                    Auto-Match
                  </Button>
                }
              >
                Bank Balance: {formatCurrency(session.bankBalance)}
                <br />
                Book Balance: {formatCurrency(session.bookBalance)}
                <br />
                Difference: {formatCurrency(Math.abs(session.difference))}
              </Alert>
            </Grid>
          )}

          {/* Transactions Grid */}
          <Grid item xs={12}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {session?.matches.map((match: ReconciliationMatch) => (
                  <TableRow key={match.bankTransaction.id}>
                    <TableCell>{formatDate(match.bankTransaction.date)}</TableCell>
                    <TableCell>{match.bankTransaction.description}</TableCell>
                    <TableCell align="right">
                      {formatCurrency(match.bankTransaction.amount)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={match.verified ? 'Verified' : 'Pending'}
                        color={match.verified ? 'success' : 'warning'}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => {
                          setSelectedTransaction(match);
                          setMatcherOpen(true);
                        }}
                      >
                        <CompareArrows />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Grid>

          {/* Exception List */}
          {hasExceptions && (
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Exceptions
              </Typography>
              {session?.exceptions?.map((exception: ReconciliationException, index: number) => (
                <Alert
                  key={index}
                  severity="warning"
                  sx={{ mb: 1 }}
                  action={
                    <Button
                      size="small"
                      onClick={() => {
                        setSelectedTransaction(exception.transaction);
                        setMatcherOpen(true);
                      }}
                    >
                      Resolve
                    </Button>
                  }
                >
                  {exception.details}
                </Alert>
              ))}
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Transaction Matcher Dialog */}
      <TransactionMatcher
        open={matcherOpen}
        transaction={selectedTransaction}
        bankTransactions={bankTransactions}
        bookTransactions={bookTransactions}
        onMatch={handleManualMatch}
        onClose={() => setMatcherOpen(false)}
      />

      {/* Audit Trail Viewer */}
      <AuditTrailViewer
        open={auditTrailOpen}
        fundId={fundId}
        onClose={() => setAuditTrailOpen(false)}
      />
    </Box>
  );
}; 